// src/views/LiquidacionDetalle.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../config/axiosConfig';
import { formatMoney } from '../utils/utils';
import CalendarioDetalleAuxiliar from './honorarios/components/CalendarioDetalleAuxiliar';

/* ─── helpers ────────────────────────────────────────────────── */
const fmtFecha = (str) => {
    if (!str) return '—';
    const [fecha, hora] = (str + '').split(' ');
    const [y, m, d] = fecha.split('-');
    return `${d}-${m}-${y}${hora ? ' ' + hora.slice(0, 5) : ''}`;
};

const fmtPeriodo = (str) => {
    if (!str) return '—';
    const [y, m] = str.split('-');
    const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
    return `${meses[parseInt(m, 10) - 1]} ${y}`;
};

const periodoLabel = (p) => p === 'night' || p === '2' || p === 2 ? 'Noche' : 'Día';
const periodoBadge = (p) => p === 'night' || p === '2' || p === 2
    ? 'bg-dark text-white'
    : 'bg-warning text-dark';

function EstadoBadge({ estado }) {
    if (!estado || estado === 'pendiente')
        return <span className="badge bg-secondary">Pendiente</span>;
    if (estado === 'borrador')
        return <span className="badge bg-warning text-dark"><i className="bi bi-pencil me-1"/>Borrador</span>;
    if (estado === 'confirmado')
        return <span className="badge bg-success"><i className="bi bi-check-circle me-1"/>Emitido</span>;
    return <span className="badge bg-light text-dark">{estado}</span>;
}

/* ─── modal turnos de un auxiliar ──────────────────────────────── */
function ModalTurnosAuxiliar({ periodo, tipo, auxiliarId, nombreAuxiliar, onClose }) {
    const [turnos, setTurnos] = useState([]);
    const [adicionales, setAdicionales] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const cargar = async () => {
            setLoading(true);
            try {
                const [resTurnos, resAdic] = await Promise.all([
                    axiosInstance.get(`/liquidacion/${periodo}/turnos`, { params: { tipo, auxiliar_id: auxiliarId } }),
                    axiosInstance.get(`/liquidacion/${periodo}/adicionales`, { params: { tipo, auxiliar_id: auxiliarId } }),
                ]);
                setTurnos(resTurnos.data.data || []);
                setAdicionales(resAdic.data.data || []);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        cargar();
    }, [periodo, tipo, auxiliarId]);

    const totalTurnos = turnos.reduce((s, t) => s + (parseFloat(t.valor) || 0) + (parseFloat(t.colacion) || 0) + (parseFloat(t.locomocion) || 0), 0);
    const totalAdic   = adicionales.reduce((s, a) => s + (parseFloat(a.valor_turno) || 0) + (parseFloat(a.valor_colacion) || 0) + (parseFloat(a.valor_locomocion) || 0), 0);

    return (
        <>
            <div className="modal-backdrop fade show" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={onClose}/>
            <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1056 }}>
                <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content border-0 shadow-lg">
                        <div className="modal-header" style={{ background: 'linear-gradient(135deg,#0d6efd,#0a58ca)' }}>
                            <div>
                                <h6 className="modal-title text-white fw-bold mb-0">
                                    <i className="bi bi-person-lines-fill me-2"/>Detalle — {nombreAuxiliar}
                                </h6>
                                <small className="text-white-50">
                                    Turnos y adicionales del snapshot ({tipo === 'adelanto' ? 'Adelanto' : 'Fin de Mes'})
                                </small>
                            </div>
                            <button className="btn-close btn-close-white" onClick={onClose}/>
                        </div>

                        <div className="modal-body p-0">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary"/>
                                </div>
                            ) : (
                                <>
                                    {/* TURNOS */}
                                    <div className="px-3 pt-3 pb-1">
                                        <h6 className="fw-bold text-secondary mb-2">
                                            <i className="bi bi-calendar-week me-1 text-primary"/>Turnos congelados
                                            <span className="badge bg-primary ms-2 rounded-pill">{turnos.length}</span>
                                        </h6>
                                    </div>
                                    {turnos.length === 0 ? (
                                        <div className="text-center py-3 text-muted small">Sin turnos registrados</div>
                                    ) : (
                                        <div className="table-responsive px-3">
                                            <table className="table table-sm table-hover align-middle mb-3">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Fecha</th>
                                                        <th>Período</th>
                                                        <th className="text-end">Valor turno</th>
                                                        <th className="text-end">Colación</th>
                                                        <th className="text-end">Locomoción</th>
                                                        <th className="text-end">Otro</th>
                                                        <th>Reemplazo</th>
                                                        <th>Notas</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {turnos.map(t => (
                                                        <tr key={t.id}>
                                                            <td className="small text-nowrap">{fmtFecha(t.fecha)}</td>
                                                            <td>
                                                                <span className={`badge ${periodoBadge(t.periodo)}`}>
                                                                    {periodoLabel(t.periodo)}
                                                                </span>
                                                            </td>
                                                            <td className="text-end small">${formatMoney(t.valor || 0)}</td>
                                                            <td className="text-end small">${formatMoney(t.colacion || 0)}</td>
                                                            <td className="text-end small">${formatMoney(t.locomocion || 0)}</td>
                                                            <td className="text-end small">${formatMoney(t.otro || 0)}</td>
                                                            <td className="small">{t.es_reemplazo ? <span className="badge bg-info text-dark">Sí</span> : '—'}</td>
                                                            <td className="small text-muted">{t.notas || '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot className="table-light fw-bold">
                                                    <tr>
                                                        <td colSpan={2}>Total turnos</td>
                                                        <td colSpan={4} className="text-end">${formatMoney(totalTurnos)}</td>
                                                        <td colSpan={2}/>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    )}

                                    {/* ADICIONALES */}
                                    <div className="px-3 pt-2 pb-1">
                                        <h6 className="fw-bold text-secondary mb-2">
                                            <i className="bi bi-plus-circle me-1 text-success"/>Adicionales congelados
                                            <span className="badge bg-success ms-2 rounded-pill">{adicionales.length}</span>
                                        </h6>
                                    </div>
                                    {adicionales.length === 0 ? (
                                        <div className="text-center py-3 text-muted small px-3 pb-3">Sin adicionales registrados</div>
                                    ) : (
                                        <div className="table-responsive px-3 pb-3">
                                            <table className="table table-sm table-hover align-middle mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Fecha</th>
                                                        <th>Período</th>
                                                        <th className="text-end">Valor turno</th>
                                                        <th className="text-end">Colación</th>
                                                        <th className="text-end">Locomoción</th>
                                                        <th className="text-end">Otro</th>
                                                        <th className="text-end">Hrs extras</th>
                                                        <th>Motivo</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {adicionales.map(a => (
                                                        <tr key={a.id}>
                                                            <td className="small text-nowrap">{fmtFecha(a.fecha)}</td>
                                                            <td>
                                                                <span className={`badge ${periodoBadge(a.periodo)}`}>
                                                                    {periodoLabel(a.periodo)}
                                                                </span>
                                                            </td>
                                                            <td className="text-end small">${formatMoney(a.valor_turno || 0)}</td>
                                                            <td className="text-end small">${formatMoney(a.valor_colacion || 0)}</td>
                                                            <td className="text-end small">${formatMoney(a.valor_locomocion || 0)}</td>
                                                            <td className="text-end small">${formatMoney(a.valor_otro || 0)}</td>
                                                            <td className="text-end small">${formatMoney(a.horas_extras || 0)}</td>
                                                            <td className="small text-muted">{a.motivo || '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot className="table-light fw-bold">
                                                    <tr>
                                                        <td colSpan={2}>Total adicionales</td>
                                                        <td colSpan={4} className="text-end">${formatMoney(totalAdic)}</td>
                                                        <td colSpan={2}/>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="modal-footer border-top-0">
                            <button className="btn btn-secondary" onClick={onClose}>
                                <i className="bi bi-x-lg me-1"/>Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

/* ─── tabla de auxiliares ─────────────────────────────────────── */
function TablaAuxiliares({ auxiliares, periodo, tipo }) {
    const [auxDetalle, setAuxDetalle] = useState(null);
    const [busqueda, setBusqueda] = useState('');

    // NUEVO: modal calendario detalle
    const [showCalendario, setShowCalendario] = useState(false);
    const [calendarioAuxID, setCalendarioAuxID] = useState(null);

    const filtrados = auxiliares.filter(a => {
        const nombre = `${a.nombre_auxiliar || ''} ${a.apepat_auxiliar || ''} ${a.apemat_auxiliar || ''}`.toLowerCase();
        return nombre.includes(busqueda.toLowerCase()) || (a.rut || '').includes(busqueda);
    });

    if (auxiliares.length === 0)
        return (
            <div className="text-center py-5 text-muted">
                <i className="bi bi-people fs-1 d-block mb-2"/>
                Sin auxiliares en esta sección.
            </div>
        );

    const totalGeneral = filtrados.reduce((s, a) => {
        const bruto  = parseFloat(a.valor_turnos || 0) + parseFloat(a.horas_extras || 0);
        const liq    = bruto * 0.8475; // aprox — solo referencia
        return s + liq + parseFloat(a.locomocion || 0) + parseFloat(a.colacion || 0);
    }, 0);

    return (
        <>
            {/* Buscador */}
            <div className="p-3 border-bottom">
                <div className="input-group input-group-sm" style={{ maxWidth: 320 }}>
                    <span className="input-group-text bg-white"><i className="bi bi-search text-muted"/></span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Filtrar por nombre o RUT…"
                        value={busqueda}
                        onChange={e => setBusqueda(e.target.value)}
                    />
                    {busqueda && (
                        <button className="btn btn-outline-secondary" onClick={() => setBusqueda('')}>
                            <i className="bi bi-x"/>
                        </button>
                    )}
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light">
                        <tr>
                            <th className="ps-3">RUT</th>
                            <th>Nombre completo</th>
                            <th className="text-center">Anticipo</th>
                            <th className="text-center">Banco</th>
                            <th className="text-end">Turnos</th>
                            <th className="text-end">Val. turnos</th>
                            <th className="text-end">Locomoción</th>
                            <th className="text-end">Colación</th>
                            <th className="text-end">Otros +</th>
                            <th className="text-end">Otros −</th>
                            <th className="text-end">Préstamos</th>
                            <th className="text-end">Vacaciones</th>
                            <th>Notas ajustes</th>
                            <th>Notas período</th>
                            <th className="text-center">Detalle</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtrados.map(a => {
                            const nombre = `${a.nombre_auxiliar || ''} ${a.apepat_auxiliar || ''} ${a.apemat_auxiliar || ''}`.trim();
                            return (
                                <tr key={a.auxiliar_id}>
                                    <td className="ps-3 small text-nowrap">{a.rut}</td>
                                    <td className="small fw-semibold">{nombre}</td>
                                    <td className="text-center">
                                        {a.anticipo
                                            ? <span className="badge bg-success">Sí</span>
                                            : <span className="badge bg-light text-dark">No</span>}
                                    </td>
                                    <td className="small text-muted">{a.banco || '—'} {a.tipo_cuenta_bancaria ? `(${a.tipo_cuenta_bancaria})` : ''}</td>
                                    <td className="text-end small">{a.cantidad_turnos}</td>
                                    <td className="text-end small">${formatMoney(a.valor_turnos || 0)}</td>
                                    <td className="text-end small">${formatMoney(a.locomocion || 0)}</td>
                                    <td className="text-end small">${formatMoney(a.colacion || 0)}</td>
                                    <td className="text-end small">${formatMoney(a.otros_suma || 0)}</td>
                                    <td className="text-end small text-danger">${formatMoney(a.otros_resta || 0)}</td>
                                    <td className="text-end small text-danger">${formatMoney(a.prestamos || 0)}</td>
                                    <td className="text-end small">${formatMoney(a.vacaciones || 0)}</td>
                                    <td className="small text-muted" style={{ minWidth: 150, whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                                        {a.notas_ajustes || '—'}
                                    </td>
                                    <td className="small text-muted" style={{ minWidth: 250, whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                                        {a.notas_periodo || '—'}
                                    </td>
                                    <td className="text-center">
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => setAuxDetalle(a)}
                                            title="Ver turnos y adicionales"
                                        >
                                            <i className="bi bi-eye"/>
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-info ms-1"
                                            onClick={() => {
                                                setCalendarioAuxID(a.auxiliar_id);
                                                setShowCalendario(true);
                                            }}
                                            title="Ver Calendario"
                                        >
                                            <i className="bi bi-calendar3"/>
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot className="table-light fw-bold">
                        <tr>
                            <td colSpan={4} className="ps-3">
                                {filtrados.length} auxiliar{filtrados.length !== 1 ? 'es' : ''}
                                {busqueda ? ` (de ${auxiliares.length})` : ''}
                            </td>
                            <td className="text-end">
                                {filtrados.reduce((s, a) => s + (parseInt(a.cantidad_turnos) || 0), 0)}
                            </td>
                            <td className="text-end">${formatMoney(filtrados.reduce((s, a) => s + parseFloat(a.valor_turnos || 0), 0))}</td>
                            <td className="text-end">${formatMoney(filtrados.reduce((s, a) => s + parseFloat(a.locomocion || 0), 0))}</td>
                            <td className="text-end">${formatMoney(filtrados.reduce((s, a) => s + parseFloat(a.colacion || 0), 0))}</td>
                            <td colSpan={5}/>
                            <td/>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {/* Modal detalle auxiliar */}
            {auxDetalle && (
                <ModalTurnosAuxiliar
                    periodo={periodo}
                    tipo={tipo}
                    auxiliarId={auxDetalle.auxiliar_id}
                    nombreAuxiliar={`${auxDetalle.nombre_auxiliar || ''} ${auxDetalle.apepat_auxiliar || ''}`.trim()}
                    onClose={() => setAuxDetalle(null)}
                />
            )}

            <CalendarioDetalleAuxiliar
                visible={showCalendario}
                onClose={() => setShowCalendario(false)}
                periodo={periodo}
                auxiliarID={calendarioAuxID}
                modo={tipo === 'adelanto' ? 'adelanto' : 'fm'}
            />
        </>
    );
}

/* ─── componente principal ─────────────────────────────────────── */
function LiquidacionDetalle() {
    const { periodo } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const tipoInicial = searchParams.get('tipo') || 'adelanto'; // 'adelanto' | 'fin_mes'
    const [tabActiva, setTabActiva] = useState(tipoInicial === 'fin_mes' ? 'fm' : 'adelanto');

    const [cabecera, setCabecera] = useState(null);
    const [detalle, setDetalle] = useState(null);
    const [loading, setLoading] = useState(true);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await axiosInstance.get(`/liquidacion/${periodo}`);
            const body = resp.data;
            if (body.error) throw new Error(body.message);
            setCabecera(body.data);
            setDetalle(body.data.detalle);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [periodo]);

    useEffect(() => { cargar(); }, [cargar]);

    const adelantoAuxiliares = detalle?.adelanto?.auxiliares || [];
    const fmAuxiliares       = detalle?.fin_mes?.auxiliares  || [];

    return (
        <div className="container-fluid py-4 px-4">

            {/* Breadcrumb */}
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                        <button className="btn btn-link p-0 small" onClick={() => navigate('/liquidaciones')}>
                            <i className="bi bi-receipt me-1"/>Liquidaciones
                        </button>
                    </li>
                    <li className="breadcrumb-item active small">{fmtPeriodo(periodo)}</li>
                </ol>
            </nav>

            {/* Encabezado */}
            <div className="d-flex align-items-center gap-2 mb-4">
                <i className="bi bi-file-earmark-text fs-3 text-primary"/>
                <div>
                    <h4 className="mb-0 fw-bold">Liquidación — {fmtPeriodo(periodo)}</h4>
                    {cabecera && (
                        <small className="text-muted">
                            Período {fmtFecha(cabecera.fecha_desde)} → {fmtFecha(cabecera.fecha_hasta)}
                        </small>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"/>
                    <div className="mt-2 text-muted small">Cargando liquidación…</div>
                </div>
            ) : !cabecera ? (
                <div className="alert alert-warning">
                    <i className="bi bi-exclamation-triangle me-2"/>No se encontró la liquidación para el período {periodo}.
                </div>
            ) : (
                <>
                    {/* INFO DE CABECERA */}
                    <div className="row g-3 mb-4">
                        {/* Adelanto */}
                        <div className="col-12 col-md-6">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                        <i className="bi bi-cash-coin text-primary fs-5"/>
                                        <h6 className="mb-0 fw-bold">Adelanto</h6>
                                        <EstadoBadge estado={cabecera.estado_adelanto}/>
                                    </div>
                                    <div className="small text-muted">
                                        <div className="mb-1">
                                            <i className="bi bi-calendar-check me-1"/>
                                            <strong>Fecha liquidación:</strong> {fmtFecha(cabecera.fecha_liquidacion_adelanto)}
                                        </div>
                                        <div>
                                            <i className="bi bi-people me-1"/>
                                            <strong>Auxiliares:</strong> {adelantoAuxiliares.length}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* FM */}
                        <div className="col-12 col-md-6">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                        <i className="bi bi-wallet2 text-success fs-5"/>
                                        <h6 className="mb-0 fw-bold">Fin de Mes</h6>
                                        <EstadoBadge estado={cabecera.estado_fm}/>
                                        {cabecera.estado_adelanto !== 'confirmado' && (
                                            <span className="badge bg-warning text-dark ms-auto small">
                                                <i className="bi bi-lock me-1"/>Esperando Adelanto
                                            </span>
                                        )}
                                    </div>
                                    <div className="small text-muted">
                                        <div className="mb-1">
                                            <i className="bi bi-calendar-check me-1"/>
                                            <strong>Fecha liquidación:</strong> {fmtFecha(cabecera.fecha_liquidacion_fm)}
                                        </div>
                                        <div>
                                            <i className="bi bi-people me-1"/>
                                            <strong>Auxiliares:</strong> {fmAuxiliares.length}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* TABS */}
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-bottom p-0">
                            <ul className="nav nav-tabs border-0 px-3 pt-3">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link fw-semibold ${tabActiva === 'adelanto' ? 'active' : ''}`}
                                        onClick={() => setTabActiva('adelanto')}
                                    >
                                        <i className="bi bi-cash-coin me-2 text-primary"/>
                                        Adelanto
                                        <span className="badge bg-primary rounded-pill ms-2">{adelantoAuxiliares.length}</span>
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link fw-semibold ${tabActiva === 'fm' ? 'active' : ''}`}
                                        onClick={() => setTabActiva('fm')}
                                    >
                                        <i className="bi bi-wallet2 me-2 text-success"/>
                                        Fin de Mes
                                        <span className="badge bg-success rounded-pill ms-2">{fmAuxiliares.length}</span>
                                    </button>
                                </li>
                            </ul>
                        </div>

                        <div className="card-body p-0">
                            {tabActiva === 'adelanto' && (
                                <TablaAuxiliares
                                    auxiliares={adelantoAuxiliares}
                                    periodo={periodo}
                                    tipo="adelanto"
                                />
                            )}
                            {tabActiva === 'fm' && (
                                <TablaAuxiliares
                                    auxiliares={fmAuxiliares}
                                    periodo={periodo}
                                    tipo="fin_mes"
                                />
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default LiquidacionDetalle;
